import React, { useEffect, useState } from 'react';
import { Box, TextField, Typography } from '@mui/material';
import { Formik, Form } from 'formik';
import { Success, Failed, Loader } from '../../components';
import {
  ProfilePageShell,
  ProfileContent,
  ProfileHead,
  ProfileSection,
  ProfileGrid,
  ProfileField,
  ProfilePhoneField,
  ProfileSelect,
  profileInputSx as inputSx,
} from '../../components/ProfileLayout';
import { cardiacTokens as T } from '../../theme/cardiacTheme';
import { Country, State, City } from 'country-state-city';
import { reportTemplateValidationSchema } from '../auth/helper/Schema';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'platform/custom/store/store';
import { hospitalTemplateApi } from '../../rtk-apis/clinics/reportTemplate';
import { getHospitalTemplateApi } from '../../rtk-apis/clinics/getReportTemplate';
import { clearState } from '../../store/slices/clinics/reportTemplate';
import { useNavigate } from 'react-router-dom';
import { uploadClinicLogoApi } from '../../rtk-apis/clinics/uploadClinicLogo';
import { getHospitalId } from '../../Utils/authUtils';

const ReportTemplate = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const hospitalId = getHospitalId();

  // Saved codes have no '+' ("61"); the options are "+61".
  const toOptionCode = (code?: string) => {
    const digits = String(code ?? '').replace(/^\+/, '');
    return digits ? `+${digits.padStart(2, '0')}` : '';
  };

  const [ReportTempleteDetails, setReportTempleteDetails] = useState({});

  const [headerStateOptions, setHeaderStateOptions] = useState([]);
  const [headerCityOptions, setHeaderCityOptions] = useState([]);

  const [headerSelectedCountry, setHeaderSelectedCountry] = useState('');
  const [headerSelectedState, setHeaderSelectedState] = useState('');
  const [headerSelectedCity, setHeaderSelectedCity] = useState('');

  const [headerCountryCode, setHeaderCountryCode] = useState('+61');

  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showSuccessProfileModal, setShowSuccessProfileModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [imagePreview, setImagePreview] = useState<string | null>(null);

  // ******************** REDUX STATES ****************************************
  const { status, loading: reportTemplateLoading } = useSelector(
    (state: RootState) => state.reportTemplateApiResponse
  );

  const {
    status: getReportTemplateStatus,
    result: getReportTemplteData,
    msg,
    loading,
    error,
  } = useSelector((state: RootState) => state.getReportTemplateApiResponse);

  // ********************************************************************************
  useEffect(() => {
    if (getReportTemplteData?.data?.length > 0) {
      const d = getReportTemplteData.data[0];
      setReportTempleteDetails(d);
      setHeaderSelectedCountry(d?.hospitalHeader?.country || d?.Country || '');
      setHeaderSelectedState(d?.hospitalHeader?.state || d?.State || '');
      setHeaderSelectedCity(d?.hospitalHeader?.city || d?.City || '');
      setImagePreview(d?.hospitalLogo || null);
      setHeaderCountryCode(
        toOptionCode(d?.hospitalHeader?.contactNumberCountryCode || d?.countryCode) || '+61'
      );
    }
  }, [getReportTemplteData]);

  const initialValues = {
    hospitalLogo: ReportTempleteDetails?.hospitalLogo || '',
    hospitalName:
      ReportTempleteDetails?.hospitalHeader?.hospitalName || ReportTempleteDetails?.name || '',
    headerCountryCode:
      toOptionCode(
        ReportTempleteDetails?.hospitalHeader?.contactNumberCountryCode ||
          ReportTempleteDetails?.countryCode
      ) || '+61',
    headerContactNumber:
      ReportTempleteDetails?.hospitalHeader?.contactNumber ||
      ReportTempleteDetails?.contactDetails ||
      '',
    headerEmailAddress:
      ReportTempleteDetails?.hospitalHeader?.email || ReportTempleteDetails?.email || '',
    headerAddress:
      ReportTempleteDetails?.hospitalHeader?.address || ReportTempleteDetails?.Address || '',
    headerZipCode:
      ReportTempleteDetails?.hospitalHeader?.zipCode || ReportTempleteDetails?.ZipCode || '',
    headerCountry:
      ReportTempleteDetails?.hospitalHeader?.country || ReportTempleteDetails?.Country || '',
    headerState: ReportTempleteDetails?.hospitalHeader?.state || ReportTempleteDetails?.State || '',
    headerCity: ReportTempleteDetails?.hospitalHeader?.city || ReportTempleteDetails?.City || '',
    website: ReportTempleteDetails?.hospitalHeader?.website || '',
    fax: ReportTempleteDetails?.hospitalHeader?.fax || '',
  };

  // ***************************** GET CITY STATES**************************************************

  useEffect(() => {
    if (headerSelectedCountry) {
      const states = State.getStatesOfCountry(headerSelectedCountry);
      setHeaderStateOptions(states);
      setHeaderCityOptions([]);
    }
  }, [headerSelectedCountry]);

  useEffect(() => {
    if (headerSelectedState) {
      const cities = City.getCitiesOfState(headerSelectedCountry, headerSelectedState);
      setHeaderCityOptions(cities);
    }
  }, [headerSelectedState, headerSelectedCountry]);

  // *********************************************************************************

  useEffect(() => {
    if (status === 'SUCCESS') {
      setShowSuccessModal(true);
    } else if (status === 'ERROR') {
      setShowErrorModal(true);
    }
    dispatch(clearState());
  }, [status, dispatch]);

  useEffect(() => {
    if (hospitalId) {
      dispatch(getHospitalTemplateApi());
    }
  }, [hospitalId]);

  const handleImageChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
    folderPath: string
  ) => {
    const file = event.target.files?.[0];
    const folderName = 'uploads';

    if (file) {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('folderName', folderName);

      const previewUrl = URL.createObjectURL(file);
      // setImagePreview(previewUrl);

      try {
        const result = await dispatch(uploadClinicLogoApi({ formData })).unwrap();
        setImagePreview(result['result']);
      } catch (error) {
        console.error('Error uploading file:', error);
      }
    }
  };

  const stripPlusFromCode = code => {
    if (!code) return '';
    return code.startsWith('+') ? code.slice(1) : code;
  };

  const handleFormSubmit = async formData => {
    try {
      const payload = {
        hospitalId: hospitalId,
        hospitalLogo: imagePreview,
        hospitalHeader: {
          hospitalName: formData?.hospitalName,
          contactNumberCountryCode: stripPlusFromCode(headerCountryCode),
          contactNumber: formData?.headerContactNumber,
          email: formData?.headerEmailAddress,
          address: formData?.headerAddress,
          zipCode: formData?.headerZipCode,
          country: formData?.headerCountry,
          state: formData?.headerState,
          city: formData?.headerCity,
          website: formData?.website,
          fax: formData?.fax,
        },
      };
      await dispatch(hospitalTemplateApi(payload)).unwrap();
    } catch (error) {
      console.error('Failed to update hospital template:', error);
    }
  };

  const handleCancel = () => {
    navigate('/reports');
  };

  return (
    <>
      <ProfilePageShell>
        <Formik
          initialValues={initialValues}
          validationSchema={reportTemplateValidationSchema}
          onSubmit={handleFormSubmit}
          enableReinitialize
        >
          {({ setFieldValue, isSubmitting, values, errors, touched, handleChange, handleBlur }) => {
            const err = (k: string) => (touched[k] && (errors[k] as string)) || '';
            const textField = (name: string, placeholder: string, extra = {}) => (
              <TextField
                fullWidth
                name={name}
                placeholder={placeholder}
                value={values[name] || ''}
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!err(name)}
                helperText={err(name)}
                sx={inputSx}
                {...extra}
              />
            );
            return (
              <Form autoComplete="off">
                <ProfileContent>
                  <ProfileHead
                    title="Update Report Template"
                    onCancel={handleCancel}
                    submitDisabled={isSubmitting}
                  />

                  <ProfileSection title="Template Details">
                    <Box sx={{ mb: '22px' }}>
                      <ProfileField
                        label="Logo"
                        required
                        hint={
                          err('hospitalLogo') ? (
                            <Box
                              component="span"
                              sx={{ color: T.red }}
                            >
                              {err('hospitalLogo')}
                            </Box>
                          ) : (
                            'Shown at the top of every report. Click to change.'
                          )
                        }
                      >
                        <Box
                          component="label"
                          htmlFor="hospitalLogo"
                          sx={{
                            width: 200,
                            height: 96,
                            borderRadius: '12px',
                            overflow: 'hidden',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            background: T.surface,
                            border: `1px dashed ${T.lineStrong}`,
                            '&:hover': { borderColor: T.teal },
                          }}
                        >
                          {imagePreview ? (
                            <Box
                              component="img"
                              src={imagePreview}
                              alt="Hospital Logo"
                              sx={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'contain',
                                background: '#fff',
                              }}
                            />
                          ) : (
                            <Typography sx={{ fontSize: 12, color: T.ink3 }}>Upload</Typography>
                          )}
                        </Box>
                        <input
                          type="file"
                          id="hospitalLogo"
                          name="hospitalLogo"
                          hidden
                          accept="image/*"
                          onChange={event => {
                            handleImageChange(event, 'uploads');
                            setFieldValue('hospitalLogo', event.currentTarget.files[0]);
                          }}
                        />
                      </ProfileField>
                    </Box>

                    <ProfileGrid>
                      <ProfileField
                        label="Hospital Name"
                        required
                      >
                        {textField('hospitalName', 'Name')}
                      </ProfileField>

                      <ProfileField label="Email Address">
                        {textField('headerEmailAddress', 'Email Address', { type: 'email' })}
                      </ProfileField>

                      <ProfileField label="Address">
                        {textField('headerAddress', 'Address')}
                      </ProfileField>

                      <ProfileField label="Zip Code">
                        {textField('headerZipCode', 'Zip Code', { inputProps: { maxLength: 10 } })}
                      </ProfileField>

                      <ProfileField label="Country">
                        <ProfileSelect
                          name="headerCountry"
                          value={headerSelectedCountry}
                          onChange={v => {
                            setHeaderSelectedCountry(v);
                            setFieldValue('headerCountry', v);
                            setFieldValue('headerState', '');
                            setFieldValue('headerCity', '');
                            setHeaderSelectedState('');
                            setHeaderSelectedCity('');
                          }}
                          options={Country.getAllCountries().map(c => ({
                            value: c.isoCode,
                            label: c.name,
                          }))}
                        />
                      </ProfileField>

                      <ProfileField label="State">
                        <ProfileSelect
                          name="headerState"
                          value={headerSelectedState}
                          onChange={v => {
                            setHeaderSelectedState(v);
                            setFieldValue('headerState', v);
                            setFieldValue('headerCity', '');
                            setHeaderSelectedCity('');
                          }}
                          options={headerStateOptions.map(st => ({
                            value: st.isoCode,
                            label: st.name,
                          }))}
                          disabled={!headerSelectedCountry}
                        />
                      </ProfileField>

                      <ProfileField label="City">
                        <ProfileSelect
                          name="headerCity"
                          value={headerSelectedCity}
                          onChange={v => {
                            setHeaderSelectedCity(v);
                            setFieldValue('headerCity', v);
                          }}
                          options={headerCityOptions.map(ci => ({
                            value: ci.name,
                            label: ci.name,
                          }))}
                          disabled={!headerSelectedState}
                        />
                      </ProfileField>

                      <ProfileField
                        label="Contact Number"
                        required
                      >
                        <ProfilePhoneField
                          name="headerContactNumber"
                          value={values.headerContactNumber}
                          countryCode={headerCountryCode}
                          onCountryCodeChange={setHeaderCountryCode}
                          onChange={digits => setFieldValue('headerContactNumber', digits)}
                          onBlur={handleBlur}
                          error={err('headerContactNumber')}
                        />
                      </ProfileField>

                      <ProfileField label="Website">{textField('website', 'Website')}</ProfileField>

                      <ProfileField label="Fax">{textField('fax', 'Fax')}</ProfileField>
                    </ProfileGrid>
                  </ProfileSection>
                </ProfileContent>
              </Form>
            );
          }}
        </Formik>
      </ProfilePageShell>
      <Loader isLoading={reportTemplateLoading} />
      <Success
        title="Clinic details updated."
        isSuccessModalOpen={showSuccessModal}
        handleCloseModal={() => setShowSuccessModal(false)}
      />
      <Success
        title="Profile picture updated."
        isSuccessModalOpen={showSuccessProfileModal}
        handleCloseModal={() => setShowSuccessProfileModal(false)}
      />
      <Failed
        title="Clinic details update failed."
        isFailedModalOpen={showErrorModal}
        handleCloseModal={() => setShowErrorModal(false)}
      />
    </>
  );
};

export default ReportTemplate;
